import { useState } from 'react';

export default function CallToAction() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    quantity: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d4sovmp1e6in3mfvki8g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          quantity: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-b from-white to-[#f5f8f2]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <h2 className="text-4xl md:text-5xl font-bold text-[#5a7c3e] mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-lg text-gray-700 mb-8 leading-relaxed">
              Request a bulk quote today and discover how Green Choice Bag can provide the perfect eco-friendly packaging solution for your business. Our team is ready to help you find the right products at the right price.
            </p>

            <div className="space-y-6 mb-10">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#5a7c3e]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-fill text-xl text-[#5a7c3e]"></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">Call for Bulk Pricing</h3>
                  <a href="tel:626-346-8383" className="text-[#5a7c3e] text-lg font-semibold hover:underline cursor-pointer">
                    626-346-8383
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#5a7c3e]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-fill text-xl text-[#5a7c3e]"></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">Email Us</h3>
                  <a href="mailto:hello@greenchoicebag.com" className="text-[#5a7c3e] hover:underline cursor-pointer">
                    hello@greenchoicebag.com
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#5a7c3e]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-fill text-xl text-[#5a7c3e]"></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">Location</h3>
                  <p className="text-gray-600">
                    City of Industry, CA
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-md">
              <h3 className="font-bold text-gray-900 mb-4 text-lg">Quick Actions</h3>
              <div className="space-y-3">
                <a
                  href="#products"
                  className="flex items-center gap-3 text-sm text-gray-700 hover:text-[#5a7c3e] transition-colors cursor-pointer group"
                >
                  <i className="ri-shopping-bag-3-fill text-lg group-hover:scale-110 transition-transform"></i>
                  <span>Browse Product Catalog</span>
                </a>
                <a
                  href="#benefits"
                  className="flex items-center gap-3 text-sm text-gray-700 hover:text-[#5a7c3e] transition-colors cursor-pointer group"
                >
                  <i className="ri-gift-fill text-lg group-hover:scale-110 transition-transform"></i>
                  <span>View Wholesale Benefits</span>
                </a>
                <a
                  href="tel:626-346-8383"
                  className="flex items-center gap-3 text-sm text-gray-700 hover:text-[#5a7c3e] transition-colors cursor-pointer group"
                >
                  <i className="ri-customer-service-2-fill text-lg group-hover:scale-110 transition-transform"></i>
                  <span>Speak with Sales Team</span>
                </a>
              </div>
            </div>
          </div>

          <div className="bg-white p-8 rounded-2xl shadow-xl">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Request a Quote</h3>
            <form onSubmit={handleSubmit} data-readdy-form id="quote-form">
              <div className="space-y-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#5a7c3e] focus:border-transparent outline-none transition-all"
                    placeholder="John Smith"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#5a7c3e] focus:border-transparent outline-none transition-all"
                    placeholder="john@company.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#5a7c3e] focus:border-transparent outline-none transition-all"
                    placeholder="(555) 123-4567"
                  />
                </div>

                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#5a7c3e] focus:border-transparent outline-none transition-all"
                    placeholder="Your Business Name"
                  />
                </div>

                <div>
                  <label htmlFor="quantity" className="block text-sm font-medium text-gray-700 mb-2">
                    Estimated Quantity *
                  </label>
                  <select
                    id="quantity"
                    name="quantity"
                    value={formData.quantity}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#5a7c3e] focus:border-transparent outline-none transition-all cursor-pointer"
                  >
                    <option value="">Select quantity range</option>
                    <option value="1-5 cartons">1-5 cartons (250-1,250 bags)</option>
                    <option value="6-10 cartons">6-10 cartons (1,500-2,500 bags)</option>
                    <option value="11-20 cartons">11-20 cartons (2,750-5,000 bags)</option>
                    <option value="20+ cartons">20+ cartons (5,000+ bags)</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Details
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#5a7c3e] focus:border-transparent outline-none transition-all resize-none"
                    placeholder="Tell us about your customization needs, preferred sizes, or any questions..."
                  ></textarea>
                  <div className="text-xs text-gray-500 mt-1">
                    {formData.message.length}/500 characters
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#5a7c3e] text-white py-4 rounded-lg font-semibold hover:bg-[#4a6c2e] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer text-base"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Quote Request'}
                </button>

                {submitStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
                    Thank you! We'll contact you within 24 hours.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    Something went wrong. Please try again or call us directly.
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
